import { sanitizeEmailHtml } from '@/lib/utils/sanitize-html'

export interface SopHeading {
  id: string
  text: string
  level: number
}

/**
 * Sanitize an SOP body (shared DOMPurify allowlist) and shape it for
 * reading: drop the empty `<p><br></p>` spacers Odoo Knowledge leaves
 * everywhere, wrap tables so they scroll in their own frame, and give
 * headings ids for the "On this page" list. Ids and classes are added after
 * sanitizing, so nothing from the stored HTML can set them.
 */
export function prepareSopHtml(
  html: string | null | undefined,
  idPrefix: string,
  headingSelector = 'h3',
): { html: string; headings: SopHeading[] } {
  if (!html) return { html: '', headings: [] }
  const doc = new DOMParser().parseFromString(`<div>${sanitizeEmailHtml(html)}</div>`, 'text/html')
  const root = doc.body.firstElementChild as HTMLElement

  root.querySelectorAll('p').forEach((p) => {
    const text = (p.textContent ?? '').replace(/\u00a0/g, ' ').trim()
    if (!text && !p.querySelector('img')) p.remove()
  })

  root.querySelectorAll('table').forEach((table) => {
    const wrap = doc.createElement('div')
    wrap.className = 'sop-table-wrap'
    table.replaceWith(wrap)
    wrap.appendChild(table)
  })

  const headings: SopHeading[] = []
  root.querySelectorAll<HTMLElement>(headingSelector).forEach((h, i) => {
    const text = (h.textContent ?? '').replace(/\s+/g, ' ').trim()
    if (!text) return
    h.id = `${idPrefix}-${i + 1}`
    headings.push({ id: h.id, text, level: Number(h.tagName.slice(1)) })
  })

  return { html: root.innerHTML, headings }
}

export function formatSopDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}
